import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { apiClient, axiosInstance } from "../services/apiClient";
import { UserRegister } from "../generated/api-client";

interface User {
  id: string;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
  teams?: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    passwordConfirm: string,
    firstname: string,
    lastname: string
  ) => Promise<void>;
  inviteUser: (email: string, role: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem("access_token");
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const userData = await apiClient.get_me_auth_me_get();
      setUser(userData);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("access_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await axiosInstance.post("/auth/login", {
        email,
        password,
      });
      // Il refresh token arriva solo come cookie HttpOnly (Set-Cookie), non nel body
      const { access_token } = response.data;
      localStorage.setItem("access_token", access_token);
      await fetchUser();
    } catch (error) {
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    passwordConfirm: string,
    firstname: string,
    lastname: string
  ) => {
    try {
      const registerBody = new UserRegister({
        email,
        password,
        password_confirm: passwordConfirm,
        firstname,
        lastname,
      });
      await apiClient.register_auth_register_post(registerBody);
    } catch (error) {
      throw error;
    }
  };

  const inviteUser = async (email: string, role: string) => {
    try {
      await axiosInstance.post("/admin/invite", { email, role });
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Nessun body: il backend legge il refresh token dal cookie, lo revoca e lo cancella
      await axiosInstance.post("/auth/logout");
    } catch (error) {
      // Ignora errori nel logout
    }
    localStorage.removeItem("access_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        register,
        inviteUser,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
